import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { StyleService } from '@b2b/features/misc/style-theme/style.service';
import { StyleTheme } from '@b2b/features/misc/style-theme/core/style-theme.model';
import { NDBSTheme } from '@b2b/features/misc/style-theme/themes/ndbs.theme';
import { TranslateService } from '@ngx-translate/core';
import { successStatus } from '@core/models/request-status.model';
import { ColorPipeService } from '@b2b/features/misc/style-theme/color.pipe';
import { StoreService } from '@b2b/features/misc/store/store.service';
import { Store, StoreTheme } from '@b2b/features/misc/store/store.model';

@Injectable({
  providedIn: 'root',
})
export class StyleThemeAdapter {
  constructor(
    private styleService: StyleService,
    private translationService: TranslateService,
    private colorPipe: ColorPipeService,
    private storeService: StoreService,
  ) {}

  getTheme(): Observable<any> {
    return this.storeService.getConfig().pipe(
      map(store => {
        const storeTheme = store ? this.parseStoreTheme(store) : null;
        const baseTheme = storeTheme ? this.buildThemeFromStore(storeTheme) : NDBSTheme.light;
        const theme = this.normalizeTheme(baseTheme);
        return { theme, ...successStatus };
      }),
    );
  }

  /**
   * Extrai o StoreTheme a partir do campo themeJson do Store retornado pela API.
   */
  private parseStoreTheme(store: Store): StoreTheme | null {
    if (!store.themeJson) return null;
    try {
      return JSON.parse(store.themeJson) as StoreTheme;
    } catch {
      console.warn('[StyleThemeAdapter] Falha ao parsear themeJson:', store.themeJson);
      return null;
    }
  }

  /**
   * Constrói um objeto de tema compatível com NDBSTheme a partir do StoreTheme.
   * Usa valores do StoreTheme quando disponíveis, com fallback para NDBSTheme.light.
   */
  private buildThemeFromStore(storeTheme: StoreTheme): any {
    const fallback = NDBSTheme.light;
    return {
      name: 'store-theme',
      core: {
        common: {
          colors: {
            text: storeTheme.textColor || fallback.core?.common?.colors?.text,
            background: storeTheme.backgroundColor || fallback.core?.common?.colors?.background,
          },
        },
        simpleCard: {
          colors: {
            background: storeTheme.cardBackground || fallback.core?.simpleCard?.colors?.background,
          },
        },
      },
      theme: {
        colors: {
          primary: storeTheme.primary || fallback.theme?.colors?.primary,
          secondary: storeTheme.secondary || fallback.theme?.colors?.secondary,
          success: storeTheme.success || fallback.theme?.colors?.success,
          warning: storeTheme.warning || fallback.theme?.colors?.warning,
          danger: storeTheme.danger || fallback.theme?.colors?.danger,
          info: storeTheme.info || fallback.theme?.colors?.info,
        },
      },
    };
  }

  private normalizeTheme(data: any): StyleTheme {
    const theme: StyleTheme = {
      name: data.name,
      core: {
        common: {
          colors: {
            text: this.colorPipe.transform(data.core.common.colors.text),
            link: this.colorPipe.transform(data.theme.colors.primary),
            background: this.colorPipe.transform(data.core.common.colors.background),
          },
        },
        simpleCard: {
          colors: {
            background: this.colorPipe.transform(data.core.simpleCard.colors.background),
            backgroundTransparent: this.styleService.addTransparency(
              this.colorPipe.transform(data.core.simpleCard.colors.background),
            ),
          },
        },
      },
      theme: {
        colors: {
          primary: this.colorPipe.transform(data.theme.colors.primary),
          secondary: this.colorPipe.transform(data.theme.colors.secondary),
          success: this.colorPipe.transform(data.theme.colors.success),
          danger: this.colorPipe.transform(data.theme.colors.danger),
          warning: this.colorPipe.transform(data.theme.colors.warning),
          info: this.colorPipe.transform(data.theme.colors.info),

          light: {
            primary: this.styleService.adjustColor(this.colorPipe.transform(data.theme.colors.primary), -10),
            secondary: this.styleService.adjustColor(this.colorPipe.transform(data.theme.colors.secondary), -10),
            success: this.styleService.adjustColor(this.colorPipe.transform(data.theme.colors.success), -10),
            danger: this.styleService.adjustColor(this.colorPipe.transform(data.theme.colors.danger), -10),
            warning: this.styleService.adjustColor(this.colorPipe.transform(data.theme.colors.warning), -10),
            info: this.styleService.adjustColor(this.colorPipe.transform(data.theme.colors.info), -10),
          },

          dark: {
            primary: this.styleService.adjustColor(this.colorPipe.transform(data.theme.colors.primary), 10),
            secondary: this.styleService.adjustColor(this.colorPipe.transform(data.theme.colors.secondary), 10),
            success: this.styleService.adjustColor(this.colorPipe.transform(data.theme.colors.success), 10),
            danger: this.styleService.adjustColor(this.colorPipe.transform(data.theme.colors.danger), 10),
            warning: this.styleService.adjustColor(this.colorPipe.transform(data.theme.colors.warning), 10),
            info: this.styleService.adjustColor(this.colorPipe.transform(data.theme.colors.info), 10),
          },
        },
      },
    };

    return theme;
  }
}
